import { RefObject, useEffect } from 'react';

const DRAG_THRESHOLD = 5;

export const useDragScroll = (ref: RefObject<HTMLElement | null>, dependency?: unknown) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startScroll = 0;
    let dragging = false;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (element.scrollWidth <= element.clientWidth) return;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = element.scrollLeft;
      dragging = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      const delta = e.clientX - startX;
      if (!dragging && Math.abs(delta) < DRAG_THRESHOLD) return;
      if (!dragging) {
        dragging = true;
        element.setPointerCapture(e.pointerId);
        element.setAttribute('data-dragging', '');
      }
      element.scrollLeft = startScroll - delta;
    };

    const onPointerEnd = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;
      pointerId = null;
      if (element.hasPointerCapture(e.pointerId)) element.releasePointerCapture(e.pointerId);
      element.removeAttribute('data-dragging');
    };

    const onClick = (e: MouseEvent) => {
      if (!dragging) return;
      dragging = false;
      e.preventDefault();
      e.stopPropagation();
    };

    const onDragStart = (e: DragEvent) => e.preventDefault();

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerEnd);
    element.addEventListener('pointercancel', onPointerEnd);
    element.addEventListener('click', onClick, true);
    element.addEventListener('dragstart', onDragStart);

    return () => {
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerEnd);
      element.removeEventListener('pointercancel', onPointerEnd);
      element.removeEventListener('click', onClick, true);
      element.removeEventListener('dragstart', onDragStart);
    };
  }, [ref, dependency]);
};
